import { db, games, submissions } from "./firebase";
import Timestamp from "../utils/Timestamp";

const serializeTimestamp = (ts) => {
  if (!ts) return;
  const { seconds, nanoseconds } = ts;
  const milliseconds = seconds * 1000 + nanoseconds / 1000000;
  const date = new Date(milliseconds);
  return date;
};

/**
 * Removes binary data (base64 images, File/Blob objects) from any value
 */
const removeBinaryData = (value) => {
  if (value === null || value === undefined) return value;
  
  // Remove base64 data URLs
  if (typeof value === 'string' && value.startsWith('data:')) {
    return undefined;
  }
  
  // Remove File/Blob objects
  if (value instanceof File || value instanceof Blob) {
    return undefined;
  }
  
  // Recursively clean arrays
  if (Array.isArray(value)) {
    const cleaned = value.map(removeBinaryData).filter(v => v !== undefined);
    return cleaned.length > 0 ? cleaned : undefined;
  }
  
  // Recursively clean objects
  if (typeof value === 'object' && value.constructor === Object) {
    const cleaned = {};
    for (const key in value) {
      const cleanedValue = removeBinaryData(value[key]);
      if (cleanedValue !== undefined) {
        cleaned[key] = cleanedValue;
      }
    }
    return Object.keys(cleaned).length > 0 ? cleaned : undefined;
  }
  
  return value;
};

/**
 * Sanitizes a round submission by removing binary data while preserving all other fields
 */
const sanitizeRound = (round) => {
  if (!round || typeof round !== 'object') return round;
  
  // Use removeBinaryData to recursively clean the entire round object
  // This preserves all fields and structure, only removing binary data
  return removeBinaryData(round);
};

const getSubmissions = async (gameId) => {
  var results = [];

  if (!gameId) {
    await submissions
      .orderBy("timestamp", "desc")
      .get()
      .then((querySnapshot) => {
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          var sub = {
            id: doc.id,
            ...data,
            date: serializeTimestamp(data.timestamp),
          };
          results.push(sub);
        });
      })
      .catch((error) => {
        console.log("Error getting documents: ", error);
      });
  } else {
    await submissions
      .orderBy("timestamp", "desc")
      .where("game", "==", db.doc(`/games/${gameId}`))
      .get()
      .then((querySnapshot) => {
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          var sub = {
            id: doc.id,
            ...data,
            date: serializeTimestamp(data.timestamp),
          };
          results.push(sub);
        });
      })
      .catch((error) => {
        console.log("Error getting documents: ", error);
      });
  }

  return results;
};

const addSubmission = (gameId, results) => {
  // Validate gameId
  if (!gameId) {
    const error = new Error("Game ID is required for submission");
    console.error("Submission error:", error);
    return Promise.reject(error);
  }

  // Sanitize submission data to remove binary content
  const sanitizedRounds = results.rounds?.map(sanitizeRound);
  const sanitizedProfile = results.profile ? removeBinaryData(results.profile) : undefined;

  const submission_data = {
    ...(sanitizedRounds && { rounds: sanitizedRounds }),
    ...(results.gameStartTimestamp && { gameStartTimestamp: results.gameStartTimestamp }),
    ...(results.gameEndTimestamp && { gameEndTimestamp: results.gameEndTimestamp }),
    ...(results.ipAddress && { ipAddress: results.ipAddress }),
    ...(sanitizedProfile && { profile: sanitizedProfile }),
    ...(results.sessionId && { sessionId: results.sessionId }),
    game: games.doc(`${gameId}`),
    timestamp: Timestamp(),
  };
  
  return submissions.add(submission_data).catch((error) => {
    console.error("Error adding submission:", error);
    console.error("Submission context:", {
      gameId,
      errorCode: error.code,
      errorMessage: error.message,
    });
    throw error;
  });
};

const nukeSubmissions = (confirm) => {
  if (confirm()) {
    let batch = db.batch();
    submissions
      .get()
      .then(function (snapshot) {
        snapshot.docs.forEach((doc) => {
          batch.delete(doc.ref);
        });
        return batch.commit();
      })
      .catch((e) => alert(e));
  } else {
  }
};

const clearSubmissionsForGame = async (gameId, confirm) => {
  if (confirm()) {
    const batch = db.batch();
    const querySnapshot = await submissions.where("game", "==", games.doc(gameId)).get();
    querySnapshot.forEach((doc) => {
      batch.delete(doc.ref);
    });
    try {
      await batch.commit();
      console.log("Submissions for game with ID", gameId, "cleared successfully.");
    } catch (error) {
      console.error("Error clearing submissions:", error);
    }
  } else {
    console.log("Clearing submissions canceled.");
  }
};

const SubmissionService = {
  getSubmissions,
  addSubmission,
  nukeSubmissions,
  clearSubmissionsForGame,
};

export default SubmissionService;
